import React, { useState } from 'react';
import {
  useCardTemplatesQuery,
  useCreateCardTemplateMutation,
} from '@trello/graphql/generated';
import { ComponentWrapper } from 'app/src/components/ComponentWrapper';
import { Spinner } from 'app/src/components/Spinner';
import { forTemplate } from 'app/common/lib/util/i18n';
import { Button } from '@trello/nachos';
import Backbone from 'app/common/vendor/backbone';
import styles from './CardTemplatesPopover.less';
import { GetFeedback } from 'app/src/components/GetFeedback';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { CardContents } from 'app/gamma/src/components/canonical-components';
import { CardLink } from '@atlassian/trello-canonical-components/src/card-front';
import { normalizeDataForCanonicalCard } from './normalizeDataForCanonicalCard';

const format = forTemplate('card-templates');

interface CardTemplatesPopoverProps {
  idBoard: string;
  idList: string;
  onSelect: (idCard: string) => void;
}

export const CardTemplates: React.FunctionComponent<
  CardTemplatesPopoverProps
> = ({ idBoard, idList, onSelect }) => {
  const { data, loading, error } = useCardTemplatesQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      boardId: idBoard,
    },
  });

  const [
    createNewTemplate,
    { loading: isCreateTemplate, error: createNewTemplateMutationError },
  ] = useCreateCardTemplateMutation();

  const [createTemplateError, setCreateTemplateError] = useState(false);

  const cards = data && data.board && data.board.cards ? data.board.cards : [];
  const templateCards = cards.filter((card) => card && card.isTemplate);

  const boardMembers =
    data && data.board && data.board.members ? data.board.members : [];

  const createTemplate = async () => {
    const repsonse = await createNewTemplate({
      variables: {
        listId: idList,
        name: format('template-title'),
        closed: false,
      },
    });

    const newTemplate = repsonse.data && repsonse.data.createCardTemplate;

    if (!newTemplate) {
      setCreateTemplateError(true);
      return;
    }

    trackUe({
      category: Noun.CARD_TEMPLATES,
      verb: Verb.CREATES,
      directObj: Noun.CARD_TEMPLATE,
      indirectObj: Noun.CARD_TEMPLATES_MENU,
      method: 'by clicking the create new template button',
    });

    Backbone.history.navigate(`/c/${newTemplate.shortLink}`, { trigger: true });
  };

  if (error || createNewTemplateMutationError || createTemplateError) {
    return <div id="cardTemplatesError">{format('something-went-wrong')}</div>;
  }

  if (!data && loading) {
    return <Spinner centered />;
  }

  const createNewTemplateButton = (
    <Button
      onClick={createTemplate}
      className={styles.createNewTemplateButton}
      appearance="primary"
      isLoading={isCreateTemplate}
      isDisabled={isCreateTemplate}
      shouldFitContainer
    >
      {format('create-a-new-template')}
    </Button>
  );

  if (templateCards.length === 0) {
    return (
      <div>
        <p className={styles.emptyStateMessage}>
          {format('you-dont-have-any-templates')}
        </p>
        {createNewTemplateButton}
      </div>
    );
  }

  return (
    <>
      <div className={styles.templateCards}>
        {templateCards.map((card) => {
          const {
            cover,
            badges,
            labels,
            members,
            stickers,
          } = normalizeDataForCanonicalCard(card, boardMembers);

          return (
            <CardLink
              key={card.id}
              className={styles.templateCard}
              onClick={() => onSelect(card.id)}
            >
              <CardContents
                name={card.name}
                cover={cover}
                badges={badges}
                isTemplate={card.isTemplate}
                labels={labels}
                expandLabels={false}
                members={members}
                stickers={stickers}
              />
            </CardLink>
          );
        })}
      </div>
      {createNewTemplateButton}
      <GetFeedback surveyName="Card Templates Feedback" />
    </>
  );
};

export const CardTemplatesPopover: React.FunctionComponent<
  CardTemplatesPopoverProps
> = (props) => (
  <ComponentWrapper>
    <CardTemplates {...props} />
  </ComponentWrapper>
);
